/**
 * SignupEmailTipEditor — admin tab for managing the short tip rendered
 * below the Email input on the customer registration form.
 *
 * Backed by `GET/PUT /api/config/signup-email-tip` (public GET, admin PUT).
 * Tiny by design: one toggle + one textarea. The tip itself is plain
 * text (no markdown) capped at 500 chars server-side to keep the
 * registration page layout stable.
 *
 * Mirrors the layout/UX conventions used by LoginBannerEditor so the
 * "Settings" tabs look consistent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Eye, EyeOff, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignupEmailTipEditor({ token }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/signup-email-tip`);
      const data = await r.json();
      setDraft({
        enabled: !!data.enabled,
        text:    data.text || '',
      });
    } catch (_) {
      setMsg({ type: 'error', text: 'Could not load signup tip config' });
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      const r = await fetch(`${API_URL}/api/config/signup-email-tip`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify(draft),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg({ type: 'success', text: 'Saved.' });
    } catch (e) {
      setMsg({ type: 'error', text: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="signup-tip-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!draft) return null;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="signup-tip-editor">
      <div>
        <h3 className="text-base font-bold text-slate-800">Signup email tip</h3>
        <p className="text-xs text-slate-500 mt-1">
          Plain-text hint rendered directly under the <code className="text-[11px] bg-slate-100 px-1 rounded">Email</code> field
          on the public registration form. Used to nudge customers towards
          trusted email providers so confirmation emails aren't dropped by
          aggressive spam filters.
        </p>
      </div>

      {/* Enabled toggle */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          data-testid="signup-tip-enabled"
          className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
        />
        <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          {draft.enabled
            ? <><Eye className="w-4 h-4 text-emerald-600" /> Tip is shown to visitors</>
            : <><EyeOff className="w-4 h-4 text-slate-400" /> Tip is hidden</>}
        </span>
      </label>

      {/* Text */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1">Tip text</label>
        <textarea
          value={draft.text}
          onChange={(e) => setDraft({ ...draft, text: e.target.value.slice(0, 500) })}
          data-testid="signup-tip-text"
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          placeholder="Tip: for new registrations, please use a trusted email provider…"
        />
        <p className="text-[11px] text-slate-400 mt-1">{draft.text.length} / 500</p>
      </div>

      {/* Live preview — exactly how the customer will see it */}
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Preview</div>
        {draft.enabled && draft.text.trim() ? (
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-[12px] text-cyan-900 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-600" />
            <span>{draft.text}</span>
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic">(nothing will be rendered)</div>
        )}
      </div>

      {/* Save + status */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button
          onClick={save}
          disabled={saving}
          data-testid="signup-tip-save"
          className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-semibold hover:bg-cyan-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        {msg.text && (
          <span className={`text-xs font-medium ${msg.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`}
            data-testid="signup-tip-msg">
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}
